import React, { useState, useRef } from "react";
import { X, Eye, Trash2, CloudDownload, CloudUpload } from "lucide-react";
import ModalMod from "../../../../../components/Model_Mod";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchModDamData,
  fetchModDemandData,
  fetchModGDamData,
  fetchModRtmData,
  ModAggregatedLoadData,
} from "../../../../../Rest_api/RestApi";
import { ErrorPopup } from "../../../../../components/ErrorHandling";
interface UpdateDataModalProps {
  isModalOpen: boolean;
  setIsModalOpen: (open: boolean) => void;
  data: any[];
  setDamData: React.Dispatch<React.SetStateAction<any[]>>;
  setGDamData: React.Dispatch<React.SetStateAction<any[]>>;
  setRtmData: React.Dispatch<React.SetStateAction<any[]>>;
  setDemandData: React.Dispatch<React.SetStateAction<any[]>>;
  setMergedData: React.Dispatch<React.SetStateAction<any[]>>;
  modalTitle: string;
  preSelectedDate?: string;
}

const UpdateMergeDataModal: React.FC<UpdateDataModalProps> = ({
  data,
  setDamData,
  setGDamData,
  setRtmData,
  setDemandData,
  setMergedData,
  isModalOpen,
  setIsModalOpen,
  modalTitle,
}) => {
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const preSelectedDate = useSelector(
    (state: any) => state.modGridcoDate.selectedDate
  );
  const modSelectedSession = useSelector(
    (state: any) => state.modGridcoSession.modSession
  );
  const dispatch = useDispatch();
  const handleDownloadTemplate = () => {
    let exportData = data && data.length > 0 ? [...data] : [];
    console.log("exportData", exportData);
    // If no data, still create 96 rows with empty demand_mw
    if (exportData.length === 0) {
      for (let i = 0; i < 96; i++) {
        exportData.push({
          time_slot: i + 1,
          demandPrice: "",
          damPrice: "",
          gdamPrice: "",
          rtmPrice: "",
        });
      }
    }
    const headerMap: { [key: string]: string } = {
      time_slot: "Time Slot",
      demandPrice: "Anticipated Demand",
      gdamPrice: "Anticipated GDAM", // renamed internal key
      damPrice: "Anticipated DAM", // renamed internal key
      rtmPrice: "Anticipated RTM", // renamed internal key
    };

    // Get the keys in the order you want
    const keys = Object.keys(headerMap);

    // Map data to only include the desired keys in the right order
    const mappedData = exportData.map((row: Record<string, any>) =>
      keys.reduce((acc, key) => {
        acc[key] = row[key];
        return acc;
      }, {} as any)
    );

    // Create worksheet with the mapped data
    const worksheet = XLSX.utils.json_to_sheet(mappedData, { header: keys });

    // Overwrite the first row with custom headers
    XLSX.utils.sheet_add_aoa(worksheet, [keys.map((k) => headerMap[k])], {
      origin: "A1",
    });

    // Create workbook and export
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");
    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });
    const blob = new Blob([excelBuffer], { type: "application/octet-stream" });
    saveAs(blob, `${modalTitle}.xlsx`);
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setUploadedFile(file);
    }
  };

  const handleViewFile = () => {
    if (uploadedFile) {
      const url = URL.createObjectURL(uploadedFile);
      window.open(url, "_blank");
      URL.revokeObjectURL(url);
      console.log(`Viewing file: ${uploadedFile.name}`);
    }
  };

  const handleDeleteFile = () => {
    setUploadedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    console.log("File deleted.");
  };

  const handleSubmit = () => {
    if (!uploadedFile) {
      setError("Please upload a file first.");
      return;
    }

    const reader = new FileReader();
    reader.onload = async (e) => {
      setLoading(true);
      try {
        const data = new Uint8Array(e.target?.result as ArrayBuffer);
        const workbook = XLSX.read(data, { type: "array" });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { defval: "" });
        const headerReverseMap: { [key: string]: string } = {
          "Time Slot": "time_slot",
          "Anticipated Demand": "demand",
          "Anticipated DAM": "dam",
          "Anticipated GDAM": "gdam",
          "Anticipated RTM": "rtm",
        };
        const firstRow = XLSX.utils.sheet_to_json(worksheet, {
          header: 1,
        })[0] as string[];
        const missingHeaders = Object.keys(headerReverseMap).filter(
          (header) => !firstRow.includes(header)
        );
        if (missingHeaders.length > 0) {
          setError(
            `Invalid file format. Missing columns: ${missingHeaders.join(", ")}`
          );
          return;
        }
        const formattedData = (jsonData as any[]).map((row) => {
          const newRow: any = {};
          Object.entries(headerReverseMap).forEach(
            ([excelHeader, internalKey]) => {
              newRow[internalKey] = row[excelHeader];
            }
          );
          return newRow;
        });

        const jsonUploadData = {
          date: preSelectedDate,
          session: modSelectedSession,
          data: formattedData,
        };
        if (modalTitle.includes("Aggregated")) {
          await ModAggregatedLoadData(jsonUploadData);

          const [demandData, gdamData, damData, rtmData] = await Promise.all([
            fetchModDemandData(preSelectedDate, modSelectedSession),
            fetchModGDamData(preSelectedDate, modSelectedSession),
            fetchModDamData(preSelectedDate, modSelectedSession),
            fetchModRtmData(preSelectedDate, modSelectedSession),
          ]);
          (window as any).resetNextTabs?.("LOAD");
          // --- Demand ---
          const demandDate = demandData.date;
          const demandTransformed = demandData.data.map((item: any) => ({
            time_slot: item.time_slot,
            date: demandDate,
            target_day: item.demand_mw,
            previous_target_day: item.previous_demand_mw,
          }));
          setDemandData(demandTransformed);

          // --- GDAM ---
          const gdamDate = gdamData.date;
          const gdamTransformed = gdamData.data.map((item: any) => ({
            time_slot: item.time_slot,
            date: gdamDate,
            wt_mcp_rs_mwh: item.mcp_rs_mwh,
            previous_mcp_rs_mwh: item.previous_mcp_rs_mwh,
          }));
          setGDamData(gdamTransformed);

          // --- DAM ---
          const damDate = damData.date;
          const damTransformed = damData.data.map((item: any) => ({
            time_slot: item.time_slot,
            date: damDate,
            wt_mcp_rs_mwh: item.mcp_rs_mwh,
            previous_mcp_rs_mwh: item.previous_mcp_rs_mwh,
          }));
          setDamData(damTransformed);

          // --- RTM ---
          const rtmDate = rtmData.date;
          const rtmTransformed = rtmData.data.map((item: any) => ({
            time_slot: item.time_slot,
            date: rtmDate,
            wt_mcp_rs_mwh: item.mcp_rs_mwh,
            previous_mcp_rs_mwh: item.previous_mcp_rs_mwh,
          }));
          setRtmData(rtmTransformed);
          setUploadedFile(null);
          setIsModalOpen(false);
        }
        setIsModalOpen(false);
      } catch (error) {
        console.error("Error processing file:", error);
        alert("There was an error processing the file. Please try again.");
        setLoading(false);
      } finally {
        setLoading(false);
      }
    };

    reader.readAsArrayBuffer(uploadedFile);
  };

  return (
    <>
      <ModalMod
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setUploadedFile(null);
        }}
        width="550px"
        showCloseButton={false}
      >
        {/* Header */}
        <div
          className="p-3"
          style={{
            backgroundColor: "#FFFFFF",
            borderBottom: "1px solid #DFEAF2",
          }}
        >
          <div className="flex items-center justify-between">
            <h2
              className="text-lg font-medium"
              style={{
                color: "#525062",
                fontFamily: "Marsden Wide",
                fontWeight: 500,
                fontSize: "18px",
                lineHeight: "100%",
                letterSpacing: "0px",
              }}
            >
              Populate Aggregated Demand Rate
            </h2>
            <button
              onClick={() => {
                setIsModalOpen(false);
                setUploadedFile(null);
              }}
              className="hover:bg-gray-200 p-1 rounded transition-colors"
              style={{ color: "#777777" }}
              aria-label="Close modal"
            >
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-2 space-y-2">
          {/* Instructions */}
          <div
            className="p-2"
            style={{ backgroundColor: "#FFFFFF", borderRadius: "8px" }}
          >
            <h3
              className="text-sm font-medium mb-2"
              style={{ color: "#6F6D7C" }}
            >
              Instruction to populate the chart
            </h3>
            <ul
              className="list-disc pl-5 space-y-1 text-sm"
              style={{ color: "#8C8A96" }}
            >
              <li>Download the excel sheet template</li>
              <li>Fill and upload the excel sheet</li>
              <li>Submit Changes</li>
            </ul>
          </div>

          {/* File actions */}
          <div className="space-y-4 border border-[#DFEAF2] p-4 rounded-xl">
            {/* Download Row */}
            <div className="flex items-center justify-between">
              <span
                className="text-sm font-medium"
                style={{ color: "#6F6D7C" }}
              >
                Excel template
              </span>
              <button
                onClick={handleDownloadTemplate}
                className="flex items-center justify-center gap-2 w-[130px] h-[39px] text-sm rounded-lg border-[0.5px] border-[#F1935C] font-medium transition-colors"
                style={{ backgroundColor: "#FFFAF6", color: "#F1935C" }}
                aria-label="Download template"
              >
                Download <CloudDownload size={18} />
              </button>
            </div>

            {/* Upload Row */}
            <div className="flex items-center justify-between">
              <span
                className="text-sm font-medium"
                style={{ color: "#6C757D" }}
              >
                Upload populated file
              </span>
              {uploadedFile ? (
                <div className="flex items-center gap-3">
                  <span
                    className="text-sm truncate block max-w-[200px]"
                    style={{ color: "#525062" }}
                  >
                    {uploadedFile.name}
                  </span>
                  <button
                    onClick={handleViewFile}
                    className=" text-orange-500 transition-colors"
                    aria-label="View file"
                  >
                    <Eye size={20} />
                  </button>
                  <button
                    onClick={handleDeleteFile}
                    className="text-[#6F6D7C] hover:text-red-500 transition-colors"
                    aria-label="Delete file"
                  >
                    <Trash2 size={20} />
                  </button>
                </div>
              ) : (
                <button
                  onClick={handleUploadClick}
                  className="flex items-center justify-center gap-2 w-[130px] h-[39px] text-sm rounded-lg border-[0.5px] border-[#34656D] font-medium transition-colors"
                  style={{ backgroundColor: "#EFFCFF", color: "#34656D" }}
                  aria-label="Upload file"
                >
                  Upload <CloudUpload size={18} />
                </button>
              )}
              <input
                type="file"
                accept=".xlsx"
                ref={fileInputRef}
                onChange={handleFileChange}
                className="hidden"
                aria-label="File input"
              />
            </div>
          </div>
        </div>

        {/* Footer */}
        <div
          className="px-4 py-3 flex justify-center"
          style={{
            backgroundColor: "#FFFFFF",
            borderTop: "1px solid #E1E5E9",
          }}
        >
          <button
            type="submit"
            disabled={loading}
            onClick={handleSubmit}
            className="flex items-center justify-center w-[130px] h-[39px] py-2.5 px-10 text-white text-sm rounded-lg font-medium hover:opacity-90 transition-opacity"
            style={{
              backgroundColor: "#FF8C42",
            }}
          >
            {loading ? "Processing..." : "Submit"}
          </button>
        </div>
      </ModalMod>
      <ErrorPopup error={error} onClose={() => setError(null)} />
    </>
  );
};

export default UpdateMergeDataModal;
